export type FetchImplementation = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

const errorCodeForStatus: Record<number, string> = {
  400: 'BAD_REQUEST', 401: 'AUTH_REQUIRED', 403: 'FORBIDDEN', 404: 'NOT_FOUND', 409: 'CONFLICT', 429: 'RATE_LIMITED',
}

export class NovelHttpError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

export class NovelHttpClient {
  private readonly fetchImpl: FetchImplementation

  constructor(private readonly config: { api: string; token: string; debugUserId?: string; fetchImpl?: FetchImplementation }) {
    this.fetchImpl = config.fetchImpl ?? fetch
  }

  async getJson<T>(pathname: string): Promise<T> {
    return this.requestJson<T>(pathname)
  }

  async postJson<T>(pathname: string, body: unknown, authenticated = true): Promise<T> {
    return this.requestJson<T>(pathname, { method: 'POST', body: JSON.stringify(body), authenticated })
  }

  private async requestJson<T>(pathname: string, options: { method?: string; body?: string; authenticated?: boolean } = {}): Promise<T> {
    const authenticated = options.authenticated ?? true
    const response = await this.fetchImpl(new URL(pathname, this.config.api), {
      method: options.method,
      headers: {
        ...(authenticated ? { Authorization: `Bearer ${this.config.token}` } : {}),
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(this.config.debugUserId ? { 'X-Debug-User-Id': this.config.debugUserId } : {}),
      }, body: options.body,
    })
    if (!response.ok) {
      let message = `Novel API request failed (${response.status})`
      try {
        const body = await response.json() as { error?: unknown }
        if (typeof body.error === 'string') message = body.error
      } catch { /* Preserve the status-derived message for non-JSON responses. */ }
      throw new NovelHttpError(errorCodeForStatus[response.status] ?? 'API_REQUEST_FAILED', response.status, message)
    }
    return response.json() as Promise<T>
  }
}
